import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { CustomerSettingsModel } from '../models/CustomerSettingsModel';
import { RESOLUTION_TYPES } from 'src/app/modules/voice-ai/resolution-types';

export enum DoNotContact {
  DMS = 'D',
  INTERNAL = 'I',
  FEDERAL_DNC = 'F',
  KNOWN_LITIGATOR = 'L',
}

@Injectable({
  providedIn: 'root',
})
export class DumbledoreService {
  private dumbledoreSetDoNotContactUrl: string;
  private dumbledoreStopCampaignProgressUrl: string;
  private dumbledoreSetVehicleInactive: string;
  private dumbledoreGetCustomerByConversationIdUrl: string;
  private dumbledoreUpdateCustomerSettingsUrl: string;
  private dumbledoreGetVehicleByLegacyConversationId: string;
  private dumbledoreGetCustomerConversations: string;
  private dumbledoreGetCustomerChannelsByCustomerId: string;
  private dumbledoreGetManifestOptions: string;
  private dumbledoreGetBotConfigurationByDealershipCode: string;

  constructor(private http: HttpClient) {
    this.dumbledoreSetDoNotContactUrl = environment.dumbledore.url + 'set-do-not-contact';
    this.dumbledoreStopCampaignProgressUrl = environment.dumbledore.url + 'stop-campaign-progress';
    this.dumbledoreSetVehicleInactive = environment.dumbledore.url + 'set-vehicle-inactive';
    this.dumbledoreGetCustomerByConversationIdUrl = environment.dumbledore.url + 'get-customer-by-conversation-id';
    this.dumbledoreUpdateCustomerSettingsUrl = environment.dumbledore.url + 'update-customer-settings';
    this.dumbledoreGetVehicleByLegacyConversationId = environment.dumbledore.url + 'get-vehicle-by-legacy-conversation-id';
    this.dumbledoreGetCustomerConversations = environment.dumbledore.url + 'get-customer-conversations';
    this.dumbledoreGetCustomerChannelsByCustomerId = environment.dumbledore.url + 'get-customer-channels-by-customer-id';
    this.dumbledoreGetManifestOptions = environment.dumbledore.url + 'get-manifest-options';
    this.dumbledoreGetBotConfigurationByDealershipCode = environment.dumbledore.url + 'get-bot-configuration-by-dealership-code';
  }

  async setDoNotContact(customerId: number, conversationId: string, doNotContact: { channelCode: string; type: DoNotContact; value: boolean }[]) {
    await Promise.all(
      doNotContact.map(async dnc => {
        const body = {
          customerId,
          channelCode: dnc.channelCode,
          legacyConversationId: conversationId,
          doNotContact: dnc.type,
          value: dnc.value,
        };

        await firstValueFrom(this.http.post(this.dumbledoreSetDoNotContactUrl, JSON.stringify(body)))
          .then(data => {
            return { status: 'success', body, data };
          })
          .catch(error => {
            console.log(error);
            return null;
          });

        return dnc;
      }),
    );
  }

  async setVehicleInactive(conversationId: string, inactive: boolean): Promise<{ status: 'success' } | null> {
    const body = { legacyConversationId: conversationId, inactive };
    const response = await firstValueFrom(this.http.post(this.dumbledoreSetVehicleInactive, JSON.stringify(body)))
      .then((): { status: 'success' } => {
        return { status: 'success' };
      })
      .catch(error => {
        console.log(error);
        return null;
      });
    return response;
  }

  async stopCampaignProgress(conversationId: string): Promise<{ status: 'success' } | null> {
    const body = {
      legacyConversationId: conversationId,
    };
    const response = await firstValueFrom(this.http.post(this.dumbledoreStopCampaignProgressUrl, JSON.stringify(body)))
      .then((): { status: 'success' } => {
        return { status: 'success' };
      })
      .catch(error => {
        console.log(error);
        return null;
      });
    return response;
  }

  async getBotConfigurationByDealershipCode(dealershipCode: string): Promise<any> {
    const body = { dealershipCode };
    return firstValueFrom(this.http.post(this.dumbledoreGetBotConfigurationByDealershipCode, JSON.stringify(body)));
  }

  async getCustomerByConversationId(conversationId: string) {
    const body = { legacyConversationId: conversationId };

    const response = await firstValueFrom(this.http.post(this.dumbledoreGetCustomerByConversationIdUrl, JSON.stringify(body)))
      .then(user => {
        return { user, status: 'success' };
      })
      .catch(error => {
        console.log(error);
        return null;
      });
    return response;
  }
  async getCustomerChannelsByCustomerId(customerId: number) {
    const body = { customerId };
    const response = await firstValueFrom(this.http.post(this.dumbledoreGetCustomerChannelsByCustomerId, JSON.stringify(body)))
      .then(user => {
        return { user, status: 'success' };
      })
      .catch(error => {
        console.log(error);
        return null;
      });
    return response;
  }

  async getCustomerConversations(customerId: number) {
    const body = { customerId, conversationStatus: null };
    const response = await firstValueFrom(this.http.post(this.dumbledoreGetCustomerConversations, JSON.stringify(body)))
      .then(user => {
        return { user, status: 'success' };
      })
      .catch(error => {
        console.log(error);
        return null;
      });
    return response;
  }

  async getVehicleByLegacyConversationId(conversationId: string) {
    const body = {
      legacyConversationId: conversationId,
    };
    const response = await firstValueFrom(this.http.post(this.dumbledoreGetVehicleByLegacyConversationId, JSON.stringify(body)))
      .then(user => {
        return { user, status: 'success' };
      })
      .catch(error => {
        console.log(error);
        return null;
      });
    return response;
  }

  async updateCustomerSettings(userId: string, customerSettings: CustomerSettingsModel): Promise<{ user: unknown; status: 'success' } | null> {
    const body = { userId, ...customerSettings };
    if (body.channel === 'SMS') {
      body.channel = 1;
    } else {
      body.channel = 2;
    }
    const response = await firstValueFrom(this.http.post(this.dumbledoreUpdateCustomerSettingsUrl, JSON.stringify(body)))
      .then((user): { user: unknown; status: 'success' } => {
        return { user, status: 'success' };
      })
      .catch(error => {
        console.log(error);
        return null;
      });
    return response;
  }

  async getManifestOptions(dealershipCode: string): Promise<string[]> {
    try {
      const body = { dealershipCode };
      const response = firstValueFrom(this.http.post<string[]>(this.dumbledoreGetManifestOptions, JSON.stringify(body)));
      return response;
    } catch (error) {
      throw new Error(error);
    }
  }

  async markAsRead(conversationId: string): Promise<void> {
    const body = { legacy_conversation_id: conversationId, status: 'read' };
    return firstValueFrom(this.http.post<void>(environment.dumbledore.url + 'update-conversation', JSON.stringify(body)));
  }

  async setConversationResolution(conversationId: string, resolution: (typeof RESOLUTION_TYPES)[0]['id']): Promise<void> {
    const body = { legacy_conversation_id: conversationId, resolution };
    return firstValueFrom(this.http.post<void>(environment.dumbledore.url + 'update-conversation', JSON.stringify(body)));
  }
}
